import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { FsApi, RequestConfig } from '@firestitch/api';
import { FsTransferService } from '@firestitch/transfer';

import { Observable } from 'rxjs';

import { FundDiseaseType, FundStatus } from '@common/enums';

import { FundQueryMethod } from '@app/common/enums';
import { Fund } from '@app/common/interfaces';


@Injectable({
  providedIn: 'root',
})
export class FundData<T = any> {

  constructor(
    private _api: FsApi,
    private _sanitizer: DomSanitizer,
    private _fsTransferService: FsTransferService,
  ) { }

  public create(data: Fund & { safeWebsiteUrl?: any; showNotes?: boolean } = { id: null }): Fund {
    data.queryMethod = data.queryMethod || FundQueryMethod.Manual;
    data.diseaseType = data.diseaseType || FundDiseaseType.All;
    data.diseases = data.diseases || [];
    data.states = data.states || [];
    data.fundSupportTypes = data.fundSupportTypes || [];
    data.fundInsuranceTypes = data.fundInsuranceTypes || [];
    data.status = data.status || FundStatus.Unknown;
    data.hasIncomeRequirement = data.hasIncomeRequirement === undefined ? true : data.hasIncomeRequirement;

    if (!data.url) {
      data.url = data.websiteUrl;
    }

    data.safeWebsiteUrl = data.websiteUrl ? this._sanitizer.bypassSecurityTrustResourceUrl(data.websiteUrl) : null;
    data.showNotes = data.queryMethod === FundQueryMethod.Manual &&
      data.status &&
      data.status !== FundStatus.Unknown &&
      !!data.notes;

    return data;
  }

  public get(id: number, query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.get(
      `funds/${id}`,
      query,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public gets(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds',
      query,
      {
        key: 'funds',
        ...config,
      },
    );
  }

  public views(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds/views',
      query,
      {
        key: 'data',
        ...config,
      },
    );
  }

  public statuses(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds/statuses',
      query,
      {
        key: 'data',
        ...config,
      },
    );
  }

  public states(query: any = {}, config: RequestConfig = {}): Observable<T> {
    return this._api.request(
      'GET',
      'funds/states',
      query,
      {
        key: 'states',
        ...config,
      },
    );
  }

  public export(
    data: any = {},
  ): void {
    this._fsTransferService.post(
      'funds/export',
      data,
    );
  }

  public statusesExport(
    fileType: 'excel' | 'pdf' = 'excel',
    data: any = {},
  ): void {
    this._fsTransferService.post(
      `funds/statuses/export/${fileType}`,
      data,
    );
  }

  public viewsExport(
    fileType: 'excel' | 'pdf' = 'excel',
    data: any = {},
  ): void {
    this._fsTransferService.post(
      `funds/views/export/${fileType}`,
      data,
    );
  }

  public viewsExportCsv(data: any = {}) {
    this._fsTransferService.post(
      'funds/views/export/csv',
      data,
    );
  }

  public put(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.put(
      `funds/${data.id}`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public post(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      'funds',
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public bulk(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      'funds/bulk',
      data,
      {
        key: null,
        ...config,
      },
    );
  }

  public postViews(fundId: number, data: any = {}, config: RequestConfig = {}): Observable<T> {
    data.fundId = fundId;

    return this._api.post(
      'funds/views',
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public delete(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.delete(
      `funds/${data.id}`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }

  public query(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      `funds/${data.id}/query`,
      data,
      {
        key: 'fund',
        ...config,
      },
    );
  }


  public debug(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      `funds/${data.id}/debug`,
      data,
      {
        ...config,
      },
    );
  }


  public search(data: any, config: RequestConfig = {}): Observable<T> {
    return this._api.post(
      'funds/search',
      data,
      {
        key: '',
        ...config,
      },
    );
  }

  public save(data: any): Observable<T> {
    return (data.id)
      ? this.put(data)
      : this.post(data);
  }

}
